import React, { useEffect, useState } from 'react';
import { FiTrash2, FiEye, FiUser, FiSend, FiMail, FiPhone, FiCalendar, FiCheckCircle, FiInfo, FiEdit3 } from 'react-icons/fi';
import { AdminSidebar } from '../../components/admin';
import { Modal } from '../../components/ui';
import api from '../../services/api'; 
import { Badge, Button, Form, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [replySubject, setReplySubject] = useState(""); 
  const [replyBody, setReplyBody] = useState(""); 
  const [btnLoading, setBtnLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', msg: '' });

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await api.get('/contact/admin/all');
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur API:", err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const showAlert = (type, msg) => {
    setAlert({ show: true, type, msg });
    setTimeout(() => setAlert({ show: false, type: '', msg: '' }), 4000);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer définitivement cette demande ?")) {
      try {
        await api.delete(`/contact/admin/${id}`);
        setMessages(messages.filter(msg => msg.id !== id));
        showAlert('success', "Demande supprimée.");
      } catch (err) {
        showAlert('danger', "Erreur lors de la suppression.");
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyBody.trim() || !replySubject.trim()) {
        alert("Le sujet et le message sont obligatoires.");
        return;
    }
    setBtnLoading(true);
    try {
      await api.post(`/contact/admin/reply/${selectedMsg.id}`, { 
        subject: replySubject, 
        reponse: replyBody 
      });
      showAlert('success', "Réponse envoyée par email au client !");
      setReplyBody("");
      setReplySubject("");
      fetchMessages();
      setShowViewModal(false);
    } catch (err) {
      showAlert('danger', "Échec de l'envoi de l'email.");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold text-dark">📩 Gestion des Demandes</h2>
          {alert.show && <Alert variant={alert.type} className="mb-0 py-2 shadow-sm">{alert.msg}</Alert>}
        </div>

        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">Client / Contact</th>
                <th>Service</th>
                <th>Date</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.length > 0 ? messages.map(m => (
                <tr key={m.id}>
                  <td className="ps-4">
                    <div className="fw-bold text-dark">{m.name}</div>
                    <div className="text-muted small"><FiMail className="me-1"/>{m.email}</div>
                  </td>
                  <td><Badge bg="light" text="dark" className="border">{m.service || 'Général'}</Badge></td>
                  <td className="small text-muted">{new Date(m.createdAt).toLocaleDateString()}</td>
                  <td className="text-end pe-4">
                    <Button variant="light" size="sm" className="me-2 shadow-sm" onClick={() => { setSelectedMsg(m); setShowViewModal(true); }}>
                      <FiEye className="text-primary" />
                    </Button>
                    <Button variant="light" size="sm" className="shadow-sm" onClick={() => handleDelete(m.id)}>
                      <FiTrash2 className="text-danger" />
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="4" className="text-center py-5 text-muted italic">Aucune demande.</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        <Modal isOpen={showViewModal} onClose={() => { setShowViewModal(false); setReplyBody(""); setReplySubject(""); }} title="Détails de la demande client">
          {selectedMsg && (
            <div className="px-2">
              <div className="info-header mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold fs-5 text-dark"><FiUser className="me-2 text-primary"/>{selectedMsg.name}</span>
                  <span className="text-muted small"><FiCalendar className="me-1"/>{new Date(selectedMsg.createdAt).toLocaleString()}</span>
                </div>
                <div className="mt-2 d-flex gap-3 text-muted small">
                   <span><FiMail className="me-1 text-primary"/>{selectedMsg.email}</span>
                   <span><FiPhone className="me-1 text-primary"/>{selectedMsg.phone || 'N/A'}</span>
                </div>
              </div>

              <div className="mb-4">
                <h6 className="fw-bold text-dark small mb-2 text-uppercase opacity-75">Message reçu :</h6>
                <div className="bubble-client p-3 rounded-3 border bg-light">
                  <p className="mb-0 small" style={{whiteSpace: 'pre-wrap'}}>{selectedMsg.message}</p>
                </div>
              </div>

              {selectedMsg.adminResponse && (
                <div className="mb-4">
                  <h6 className="fw-bold text-success small mb-2 text-uppercase opacity-75"><FiCheckCircle className="me-1"/> Réponse envoyée :</h6>
                  <div className="bubble-admin-sent p-3 rounded-3 border-start border-4 border-success bg-white shadow-sm">
                    <p className="fw-bold small mb-1">Sujet : {selectedMsg.adminResponseSubject || 'Sans sujet'}</p>
                    <p className="mb-0 small" style={{whiteSpace: 'pre-wrap'}}>{selectedMsg.adminResponse}</p>
                    <div className="text-end border-top pt-2 mt-2">
                      <small className="text-muted italic">Le {selectedMsg.repliedAt ? new Date(selectedMsg.repliedAt).toLocaleString() : '---'}</small>
                    </div>
                  </div>
                </div>
              )}

              {!selectedMsg.adminResponse && (
                <div className="reply-section">
                  <h6 className="fw-bold text-primary mb-2 small-title"><FiSend className="me-2"/>Répondre au client</h6>
                  <div className="reply-box p-3 rounded-3 border bg-white shadow-sm">
                    <div className="mb-3 border-bottom pb-2">
                        <Form.Control 
                            type="text"
                            placeholder="Objet du mail..."
                            className="border-0 shadow-none p-0 fw-bold"
                            value={replySubject}
                            onChange={(e) => setReplySubject(e.target.value)}
                        />
                    </div>
                    <Form.Control 
                      as="textarea" 
                      rows={6} 
                      placeholder="Écrivez votre message ici..." 
                      className="border-0 shadow-none p-0 mb-3 small"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      style={{whiteSpace: 'pre-wrap'}}
                    />
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <small className="text-muted italic" style={{fontSize: '0.7rem'}}>Le client recevra ce mail immédiatement.</small>
                      <Button variant="primary" size="sm" className="px-4 rounded-pill shadow-sm" disabled={btnLoading || !replyBody || !replySubject} onClick={handleSendReply}>
                        {btnLoading ? <Spinner size="sm" /> : <><FiSend className="me-2"/>Envoyer le Mail</>}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>

      <style>{`
        .admin-container { display: flex; min-height: 100vh; background: #f8fafc; font-family: 'Inter', sans-serif; }
        .admin-content { flex: 1; }
        .bubble-client { background: #f1f5f9; border: 1px solid #e2e8f0; font-size: 0.9rem; color: #334155; }
        .bubble-admin-sent { background: #ffffff; border: 1px solid #dcfce7; font-size: 0.9rem; }
        .reply-box { transition: 0.3s; border: 1px solid #e2e8f0; }
        .reply-box:focus-within { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .table thead th { font-size: 0.75rem; text-transform: uppercase; color: #64748b; letter-spacing: 1px; }
      `}</style>
    </div>
  );
};

export default AdminMessages;